import React, { useState, useRef, useEffect } from 'react';
import { TextField, CircularProgress } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

/**
 * Selector múltiple con chips que además acepta PEGAR una lista de valores.
 *
 * Es el `Autocomplete multiple` de MUI que ya se usa en los filtros de grilla, con tres
 * mejoras (VEC-3555):
 *
 *  1. Las opciones se buscan en el servidor a medida que se tipea (`onSearch`), en vez
 *     de precargar la lista completa. En flotas grandes precargar era inviable.
 *  2. Al pegar una lista separada por coma o punto y coma y salir del campo (blur), los
 *     valores se resuelven contra el backend (`onResolve`): los encontrados se vuelven
 *     chips y los NO encontrados quedan escritos como texto, para que el usuario vea
 *     cuáles fallaron.
 *  3. Lo que queda como texto suelto no se pierde: se entrega por `onTextoChange` para
 *     que el consumidor lo use como búsqueda parcial.
 *
 * DECISIÓN: el resolver se dispara solo si el texto CONTIENE un delimitador, es decir
 * si es una lista. Un valor único tipeado se deja como texto para que siga funcionando
 * la búsqueda parcial de siempre (escribir "AC" y que traiga todo lo que contenga AC).
 *
 * @param seleccionados  array de {value, label, tipo} — los chips
 * @param onSeleccionChange  (nuevoArray) => void
 * @param texto          el texto libre que quedó sin resolver
 * @param onTextoChange  (nuevoTexto) => void
 * @param onSearch       (busqueda) => Promise<Array<{value,label,tipo}>>
 * @param onResolve      (valores[]) => Promise<{encontrados: [...], noEncontrados: [...]}>
 * @param label / placeholder / disabled
 */
const DELIMITADORES = /[,;]/;
const DEBOUNCE_MS = 350;
const MINIMO_PARA_BUSCAR = 2;

const PastableMultiAutocomplete = (props) => {
	const {
		seleccionados,
		onSeleccionChange,
		texto,
		onTextoChange,
		onSearch,
		onResolve,
		label,
		placeholder,
		disabled,
	} = props;

	const [opciones, setOpciones] = useState([]);
	// Muestra que se pide una sola vez, para que el desplegable no abra vacío cuando el
	// usuario todavía no escribió nada.
	const [defaults, setDefaults] = useState(null);
	const [cargando, setCargando] = useState(false);
	const [resolviendo, setResolviendo] = useState(false);
	const timerRef = useRef(null);
	const montadoRef = useRef(true);
	// Último texto que ya se resolvió. Los no encontrados se devuelven CON el delimitador
	// (decisión de producto: el usuario tiene que ver dónde empieza y termina cada valor),
	// así que sin esto cada blur posterior volvería a resolver la misma lista.
	const ultimoResueltoRef = useRef(null);

	useEffect(() => {
		return () => {
			montadoRef.current = false;
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	// Búsqueda incremental con debounce. No se busca si el texto es una lista pegada:
	// en ese caso lo que corresponde es resolver al salir del campo, no autocompletar.
	const buscarConDebounce = (busqueda) => {
		if (timerRef.current) clearTimeout(timerRef.current);

		if (DELIMITADORES.test(busqueda)) {
			// Es una lista pegada: no se autocompleta, se resuelve al salir del campo.
			setOpciones([]);
			return;
		}

		if (!busqueda || busqueda.length < MINIMO_PARA_BUSCAR) {
			// Borró lo que había escrito: se vuelve a la muestra inicial en vez de dejar
			// el desplegable vacío.
			setOpciones(defaults || []);
			return;
		}

		timerRef.current = setTimeout(() => {
			setCargando(true);
			Promise.resolve(onSearch(busqueda))
				.then((resultado) => {
					if (!montadoRef.current) return;
					setOpciones(Array.isArray(resultado) ? resultado : []);
				})
				.catch(() => {
					if (montadoRef.current) setOpciones([]);
				})
				.finally(() => {
					if (montadoRef.current) setCargando(false);
				});
		}, DEBOUNCE_MS);
	};

	// Al abrir el desplegable sin texto se muestra la muestra inicial. Se pide una sola
	// vez por montaje y queda cacheada.
	const handleOpen = () => {
		if ((texto || '').length >= MINIMO_PARA_BUSCAR) return;
		if (defaults !== null) {
			setOpciones(defaults);
			return;
		}
		setCargando(true);
		Promise.resolve(onSearch(''))
			.then((resultado) => {
				if (!montadoRef.current) return;
				const lista = Array.isArray(resultado) ? resultado : [];
				setDefaults(lista);
				setOpciones(lista);
			})
			.catch(() => {
				if (montadoRef.current) setDefaults([]);
			})
			.finally(() => {
				if (montadoRef.current) setCargando(false);
			});
	};

	const yaEstaSeleccionado = (entidad) =>
		(seleccionados || []).some((e) => e.tipo === entidad.tipo && String(e.value) === String(entidad.value));

	// Al salir del campo: si hay una lista pegada, se resuelve.
	const handleBlur = () => {
		const actual = texto || '';
		if (!DELIMITADORES.test(actual)) return;
		if (actual === ultimoResueltoRef.current) return;
		ultimoResueltoRef.current = actual;

		const valores = actual.split(DELIMITADORES).map((v) => v.trim()).filter((v) => v !== '');
		if (!valores.length) {
			onTextoChange('');
			return;
		}

		setResolviendo(true);
		Promise.resolve(onResolve(valores))
			.then((res) => {
				if (!montadoRef.current) return;
				const encontrados = (res && res.encontrados) ? res.encontrados : [];
				const noEncontrados = (res && res.noEncontrados) ? res.noEncontrados : [];

				// Los duplicados respecto de lo ya elegido se ignoran en silencio.
				const nuevos = encontrados
					.filter((e) => !yaEstaSeleccionado(e))
					.map((e) => ({ value: e.value, label: e.label, tipo: e.tipo }));

				if (nuevos.length) onSeleccionChange((seleccionados || []).concat(nuevos));
				// Lo que no matcheó queda escrito, para que se vea qué falló.
				// El texto resultante ES el nuevo "último resuelto": los no encontrados vuelven
				// CON el delimitador (decisión de producto), así que sin esto el próximo blur
				// los resolvería de nuevo aunque el usuario no haya tocado nada.
				const restante = noEncontrados.join('; ');
				ultimoResueltoRef.current = restante;
				onTextoChange(restante);
			})
			.catch(() => {
				/* si falla la resolución se deja el texto tal como está */
			})
			.finally(() => {
				if (montadoRef.current) setResolviendo(false);
			});
	};

	return (
		<Autocomplete
			size="small"
			multiple
			freeSolo
			// Con freeSolo, MUI oculta la flecha del desplegable. Se fuerza para que el
			// campo se lea como selector, igual que los demás filtros de la grilla.
			forcePopupIcon
			disabled={disabled}
			options={opciones}
			value={seleccionados || []}
			inputValue={texto || ''}
			loading={cargando || resolviendo}
			onOpen={handleOpen}
			// Subtítulo por tipo de entidad (Móviles / Llantas / Personas), como tenía el
			// selector anterior. Requiere que las opciones lleguen ordenadas por tipo, o
			// MUI repite el encabezado: de eso se encarga el backend.
			groupBy={(option) => (option && option.tipo ? option.tipo : '')}
			ListboxComponent={(listboxProps) => <ul {...listboxProps} style={{ maxHeight: '230px', overflow: 'auto' }} />}
			// El filtrado es del servidor: MUI no debe volver a filtrar lo que ya llegó.
			filterOptions={(x) => x}
			getOptionLabel={(option) => (option && option.label ? option.label : '')}
			getOptionSelected={(option, valor) =>
				option && valor && option.tipo === valor.tipo && String(option.value) === String(valor.value)
			}
			// Con freeSolo, MUI intentaría crear un chip con el texto tipeado. Se aceptan
			// SOLO las opciones (objetos): un valor no encontrado nunca se vuelve chip.
			onChange={(e, valor, motivo) => {
				const soloEntidades = (valor || []).filter((v) => v && typeof v === 'object');
				onSeleccionChange(soloEntidades);

				// Al ELEGIR una opción, lo tipeado ya cumplió su función de búsqueda: se
				// limpia, si no queda "AC" suelto en el input después de elegir "AC123" y
				// el usuario tiene que borrarlo a mano.
				//
				// Se hace acá y no en onInputChange a propósito: `reason: 'reset'` de
				// onInputChange también se dispara en otros momentos, y limpiar ahí borraría
				// el texto cuando el usuario escribe algo y sale del campo SIN elegir nada
				// — que es justamente el caso de la búsqueda parcial.
				//
				// Y se filtra por motivo para no pisar el texto al quitar un chip: si el
				// usuario tiene un valor no encontrado escrito y borra un chip, lo escrito
				// tiene que quedar.
				if (motivo === 'select-option') {
					onTextoChange('');
					setOpciones(defaults || []);
				}
			}}
			onInputChange={(e, valor, motivo) => {
				if (motivo === 'reset') return; // no pisar el texto al elegir una opción
				onTextoChange(valor);
				buscarConDebounce(valor);
			}}
			onBlur={handleBlur}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					label={label}
					placeholder={placeholder}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<React.Fragment>
								{(cargando || resolviendo) ? <CircularProgress color="inherit" size={16} /> : null}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
				/>
			)}
		/>
	);
};

export default PastableMultiAutocomplete;
